// Grid spacing auto-fill, element length warnings, maximum rows/columns (with the linked-to-max behaviour), gammaM.

import { currentAlphaElem1, currentAlphaElem2, currentFi1, currentFi2 } from "./angles";
import { tryCurrentClt } from "./clt";
import { isSteelMat, pInt, type EngineCtx } from "./ctx";
import {
  element1Lines,
  element2Bounds,
  elem1Trig,
  gridAxis2,
  isPointOk,
  isThetaParallel,
  maxScrewGrid,
  passiGrigliaObliqua,
} from "./grid";
import { gammaMValue } from "./materials";
import { computeSpacingAt, currentDiametro, etaSpacingClt, steelPlateMinDistances } from "./spacing";

// Value written into a number field with toFixed and read back with parseFloat ("NaN"/"Infinity" are not valid
// numbers: the field becomes empty).
export const fixed = (x: number, dec: number): number => (Number.isFinite(x) ? Number(x.toFixed(dec)) : NaN);

// Runs on every render (also with "Distanze minime automatiche" off): with auto it fills s1/s2 and the edge/end
// distances; a1/a2 of each element are always written (informative, read-only); without auto it only warns when
// the manual s1/s2 are below the recomputed minimum.
export function autoFillSpacing(ctx: EngineCtx): void {
  const v = ctx.v;
  const auto = v.autoSpacing;
  const theta = v.thetaNum;
  const alphaElem1 = currentAlphaElem1(ctx);
  const alphaElem2 = currentAlphaElem2(ctx);
  const d = currentDiametro(ctx);
  const fi1 = currentFi1(ctx),
    fi2 = currentFi2(ctx);
  // φ rotates the force/grain angle of the distance formulas, not the geometric θ (sin θ' of the grid steps)
  const alphaGrainElem1 = alphaElem1 - fi1;
  const alphaGrainElem2 = alphaElem2 - fi2;
  const clt1 = tryCurrentClt(ctx, 1);
  const clt2 = tryCurrentClt(ctx, 2);
  // CLT: ETA-11/0030 Tab. B1 distances replace the generic ones
  const sp1 = clt1 ? etaSpacingClt(clt1.latoInserimento, d) : computeSpacingAt(ctx, alphaGrainElem1, 1);
  const sp2 = clt2 ? etaSpacingClt(clt2.latoInserimento, d) : computeSpacingAt(ctx, alphaGrainElem2, 2);
  const isSteel1 = isSteelMat(v.materiale1);
  const { passoAsse1, passoAsse2 } = passiGrigliaObliqua(sp1, sp2, theta, isSteel1, fi1, fi2);
  if (auto) {
    v.s11 = fixed(passoAsse1, 0);
    v.s12 = fixed(passoAsse2, 0);
    // steel plate: a3,t,1/a4,t,1/a4,c,1 from the hole criterion (steelPlateMinDistances), not EC5 Table 8.2
    const dist1 = isSteel1 ? steelPlateMinDistances(currentDiametro(ctx)) : sp1;
    v.a4t1 = fixed(dist1.a4t, 0);
    v.a4c1 = fixed(dist1.a4c, 0);
    v.a3t1 = fixed(dist1.a3t, 0);
    v.a4t2 = fixed(sp2.a4t, 0);
    v.a4c2 = fixed(sp2.a4c, 0);
    v.a3t2 = fixed(sp2.a3t, 0);
    v.a3c2 = fixed(sp2.a3c, 0);
  }
  v.a1e1 = fixed(sp1.a1, 0);
  v.a2e1 = fixed(sp1.a2, 0);
  v.a1e2 = fixed(sp2.a1, 0);
  v.a2e2 = fixed(sp2.a2, 0);
  const texts = ctx.ui.texts;
  const tl = ctx.t.engine.layout;
  if (!auto) {
    const s11Val = v.s11;
    texts.s11Warn =
      Number.isFinite(s11Val) && s11Val < passoAsse1 - 1e-6 ? tl.spacingBelowMin(`s1=${s11Val.toFixed(1)}`, passoAsse1.toFixed(1)) : null;
    const s12Val = v.s12;
    texts.s12Warn =
      Number.isFinite(s12Val) && s12Val < passoAsse2 - 1e-6 ? tl.spacingBelowMin(`s2=${s12Val.toFixed(1)}`, passoAsse2.toFixed(1)) : null;
  } else {
    texts.s11Warn = null;
    texts.s12Warn = null;
  }
}

// Spacing fields read-only with automatic distances; "spread" only with automatic distances.
export function toggleSpacingEditable(ctx: EngineCtx): void {
  const auto = ctx.v.autoSpacing;
  (["s11", "s12", "a4t1", "a4c1", "a3t1", "a4t2", "a4c2", "a3t2", "a3c2"] as const).forEach((id) => {
    ctx.ui.disabled[id] = auto;
  });
  ctx.ui.disabled.spreadViti = !auto;
}

// The element length (d1/d2) must contain the required end distance (d1 ≥ a3,t,1; d2 ≥ a3,t,2 for θ ≤ 90°, a3,c,2
// otherwise): soft constraint (input min + warning), not for continuous elements.
export function updateLengthWarnings(ctx: EngineCtx): void {
  const v = ctx.v,
    { texts, limits } = ctx.ui;
  const tl = ctx.t.engine.layout;
  const theta = v.thetaNum;
  const d1 = v.d1,
    d2 = v.d2;
  const a3t1 = v.a3t1,
    a3t2 = v.a3t2,
    a3c2 = v.a3c2;
  if (!v.continuo1 && Number.isFinite(a3t1)) {
    limits.d1Min = a3t1;
    texts.d1Warn =
      Number.isFinite(d1) && d1 < a3t1 ? tl.lengthBelowEndDistance("d1", d1, `a3,t,1 = ${a3t1.toFixed(1)}`) : null;
  } else {
    limits.d1Min = null;
    texts.d1Warn = null;
  }
  const branchTeso = Number.isFinite(theta) && theta <= 90;
  const req2 = Number.isFinite(theta) ? (branchTeso ? a3t2 : a3c2) : NaN;
  const req2Label = branchTeso ? "a3,t,2" : "a3,c,2";
  if (!v.continuo2 && Number.isFinite(req2)) {
    limits.d2Min = req2;
    if (Number.isFinite(d2) && d2 < req2) {
      texts.d2Warn = ctx.ui.disabled.d2
        ? // CLT narrow: d2 = b2/2 is automatic — the remedy is the panel thickness (user request: warning only)
          tl.cltHalfThickness(d2.toFixed(1), `${req2Label} = ${req2.toFixed(1)}`, (2 * req2).toFixed(0))
        : tl.lengthBelowEndDistance("d2", d2, `${req2Label} = ${req2.toFixed(1)}`);
    } else {
      texts.d2Warn = null;
    }
  } else {
    limits.d2Min = null;
    texts.d2Warn = null;
  }
}

export type MaxRowsCols = {
  rows: number;
  cols: number;
  total: number;
  capped: boolean;
  cornerDistanceCapped: boolean;
  maxCornerDistanceRadente: number | null;
  passoAsse1: number;
  passoAsse2: number;
  offsetX: number; // recommended "Spostamento centro" (0,0 when the grid centred on the axes crossing is best)
  offsetY: number;
};

// Maximum admissible rows × cols at the code minimum steps, using the edge/end distances currently in the fields.
export function computeMaxRowsCols(ctx: EngineCtx): MaxRowsCols {
  const v = ctx.v;
  const theta = v.thetaNum;
  const { b1, d1, a4t1, a4c1, a3t1, b2, d2, a4t2, a4c2, a3t2, a3c2 } = v;
  const d = currentDiametro(ctx);
  const fi1 = currentFi1(ctx),
    fi2 = currentFi2(ctx);
  const clt1 = tryCurrentClt(ctx, 1);
  const clt2 = tryCurrentClt(ctx, 2);
  const sp1 = clt1 ? etaSpacingClt(clt1.latoInserimento, d) : computeSpacingAt(ctx, currentAlphaElem1(ctx) - fi1, 1);
  const sp2 = clt2 ? etaSpacingClt(clt2.latoInserimento, d) : computeSpacingAt(ctx, currentAlphaElem2(ctx) - fi2, 2);
  // NB: skipElem1Spacing is NOT passed here (pre-existing behaviour of the source), only autoFillSpacing applies it
  const { passoAsse1, passoAsse2, crossDominant } = passiGrigliaObliqua(sp1, sp2, theta, undefined, fi1, fi2);
  const lines1 = element1Lines(theta, b1, d1, a4t1, a4c1, a3t1, v.continuo1, b2);
  const bounds2 = element2Bounds(b2, d2, a4t2, a4c2, a3t2, a3c2, theta, v.continuo2, b1);
  // GRAZING ANGLE (θ<=20° or θ>=160°): with the checkbox, the real distance between the extreme screws <= 2·b1
  const angoloRadente = theta <= 20 || theta >= 160;
  const maxCornerDistanceRadente = v.limitaGrigliaRadente && angoloRadente ? 2 * b1 : null;
  const best = maxScrewGrid(theta, lines1, bounds2, passoAsse1, passoAsse2, {
    crossDominant,
    ...(maxCornerDistanceRadente != null ? { maxCornerDistance: maxCornerDistanceRadente } : {}),
  });

  // NON-CENTRED REGULAR GRID: the admissible envelope is convex but not symmetric about the axes crossing, so a
  // larger parallelogram with the same uniform steps may fit when translated: try a few extra rows/cols and shifts
  // by half steps along u (e1) and w (grid axis 2), checking only the 4 corners. Small search: runs on every render.
  const { c: c1, s: s1 } = elem1Trig(theta);
  const { ex: e2x, ey: e2y } = gridAxis2(theta, c1, s1, crossDominant);
  const cornersShifted = (rows: number, cols: number, uShift: number, wShift: number) => {
    const uHalf = ((rows - 1) * passoAsse1) / 2,
      wHalf = ((cols - 1) * passoAsse2) / 2;
    const us = [-uHalf + uShift, uHalf + uShift];
    const ws = [-wHalf + wShift, wHalf + wShift];
    const pts: { x: number; y: number }[] = [];
    for (const u0 of us) for (const w0 of ws) pts.push({ x: -u0 * s1 + w0 * e2x, y: u0 * c1 + w0 * e2y });
    return pts;
  };
  const allAdmissible = (pts: { x: number; y: number }[]) => pts.every((p) => isPointOk(p.x, p.y, lines1, bounds2));
  const maxPairwiseDist = (pts: { x: number; y: number }[]) => {
    let m = 0;
    pts.forEach((p, i) => {
      for (const q of pts.slice(i + 1)) m = Math.max(m, Math.hypot(p.x - q.x, p.y - q.y));
    });
    return m;
  };
  let bestOverall = { rows: best.rows, cols: best.cols, uShift: 0, wShift: 0, total: best.total };
  if (best.total > 0) {
    const SHIFTS = [0, 0.5, 1, 1.5, 2, -0.5, -1, -1.5, -2]; // multiples of half step
    for (let extraRows = 0; extraRows <= 3; extraRows++) {
      for (let extraCols = 0; extraCols <= 3; extraCols++) {
        const rows = best.rows + extraRows,
          cols = best.cols + extraCols;
        const total = rows * cols;
        if (total <= bestOverall.total) continue;
        for (const su of SHIFTS) {
          for (const sw of SHIFTS) {
            const uShift = su * passoAsse1,
              wShift = sw * passoAsse2;
            const pts = cornersShifted(rows, cols, uShift, wShift);
            if (!allAdmissible(pts)) continue;
            if (maxCornerDistanceRadente != null && maxPairwiseDist(pts) > maxCornerDistanceRadente) continue;
            if (total > bestOverall.total) bestOverall = { rows, cols, uShift, wShift, total };
          }
        }
      }
    }
  }
  // ELEMENT 2 CLT NARROW (user request): ONE row of screws on element 2's axis (b2 = panel thickness), no lateral
  // shift: "columns" (axis 2 = (0,1)) when θ is not 0°/180°, "rows" (along e1) at θ = 0°/180°. Maximum number of
  // admissible screws, preferring the smallest shifts along the axis.
  if (clt2 && clt2.latoInserimento === "narrow") {
    const alongE1 = isThetaParallel(theta);
    const SH = [0, 0.5, -0.5, 1, -1, 1.5, -1.5, 2, -2];
    let one: typeof bestOverall | null = null;
    for (let n = 1; n <= 60; n++) {
      let found: { uShift: number; wShift: number } | null = null;
      for (const sh of SH) {
        const pts = alongE1 ? cornersShifted(n, 1, sh * passoAsse1, 0) : cornersShifted(1, n, 0, sh * passoAsse2);
        if (!allAdmissible(pts)) continue;
        if (maxCornerDistanceRadente != null && maxPairwiseDist(pts) > maxCornerDistanceRadente) continue;
        found = alongE1 ? { uShift: sh * passoAsse1, wShift: 0 } : { uShift: 0, wShift: sh * passoAsse2 };
        break;
      }
      if (!found) break;
      one = { rows: alongE1 ? n : 1, cols: alongE1 ? 1 : n, uShift: found.uShift, wShift: found.wShift, total: n };
    }
    bestOverall = one || { rows: 1, cols: 1, uShift: 0, wShift: 0, total: 0 };
  }
  const offsetX = -bestOverall.uShift * s1 + bestOverall.wShift * e2x;
  const offsetY = bestOverall.uShift * c1 + bestOverall.wShift * e2y;
  return {
    rows: bestOverall.rows,
    cols: bestOverall.cols,
    total: bestOverall.total,
    capped: best.capped,
    cornerDistanceCapped: best.cornerDistanceCapped,
    maxCornerDistanceRadente,
    passoAsse1,
    passoAsse2,
    offsetX,
    offsetY,
  };
}

// Sets the max of rows/cols and, while a field is linked to the maximum (no manual edit since) or exceeds it,
// brings it to the maximum; the centre offset follows only when rows, cols AND offset are all linked.
// "Limita righe/colonne" off: no limit, the fields are not touched.
export function applyMaxRowsCols(ctx: EngineCtx): MaxRowsCols | null {
  const v = ctx.v,
    { visible, texts, limits } = ctx.ui;
  const limitOn = v.limitGriglia;
  visible.limitGrigliaWarn = !limitOn;
  let maxRC: MaxRowsCols;
  try {
    maxRC = computeMaxRowsCols(ctx);
  } catch {
    texts.limitaGrigliaRadenteWarn = null;
    return null;
  }
  texts.limitaGrigliaRadenteWarn = maxRC.cornerDistanceCapped
    ? ctx.t.engine.layout.cornerCapped(maxRC.rows, maxRC.cols, maxRC.maxCornerDistanceRadente!.toFixed(1))
    : null;
  if (!limitOn) {
    limits.rowsMax = null;
    limits.colsMax = null;
    return maxRC;
  }
  if (!(maxRC.rows >= 1) || !(maxRC.cols >= 1)) return maxRC;
  limits.rowsMax = maxRC.rows;
  limits.colsMax = maxRC.cols;
  const curRows = pInt(v.rows);
  const curCols = pInt(v.cols);
  if (v.rowsLinkedToMax || !(curRows > 0) || curRows > maxRC.rows) v.rows = maxRC.rows;
  if (v.colsLinkedToMax || !(curCols > 0) || curCols > maxRC.cols) v.cols = maxRC.cols;
  if (v.centroLinkedToMax && v.rowsLinkedToMax && v.colsLinkedToMax) {
    v.centroOrizz = fixed(maxRC.offsetX, 1);
    v.centroVert = fixed(maxRC.offsetY, 1);
  }
  return maxRC;
}

export function updateGammaMColonnaVisibility(ctx: EngineCtx): void {
  ctx.ui.visible.gammaMColonnaWrap = ctx.v.normativaGammaM === "NTC2018";
}
export function autoFillGammaM(ctx: EngineCtx): void {
  updateGammaMColonnaVisibility(ctx);
  if (!ctx.v.gammaMAuto) return;
  ctx.v.gammaM = fixed(gammaMValue(ctx.cat, ctx.v.normativaGammaM, ctx.v.gammaMColonna), 2);
}
export function toggleGammaMEditable(ctx: EngineCtx): void {
  ctx.ui.disabled.gammaM = ctx.v.gammaMAuto;
  if (ctx.v.gammaMAuto) autoFillGammaM(ctx);
}
